/*
 * synastry/index.js — UI entry for /synastry.
 *
 * Loaded through @moduleGraph so relative imports keep their version stamp.
 * The astronomy is the natal chart's; the comparison is engine.js; this file
 * owns the two forms, the result view and nothing else.
 *
 * The tool refuses to print a compatibility percentage — see engine.js for why
 * that number cannot be computed honestly.
 *
 * ⚠️ It used to print a DIFFERENT percentage in the place where readers expect
 * that one. The headline was "how much of this comparison the birth data can
 * carry", rendered as a 44px green "100%" directly under the two names, with
 * the disclaimer beneath it in 12px grey. That is the layout of a compatibility
 * score, and a reader who came from the query "совместимость по дате рождения"
 * reads the layout, not the caption. Refusing to invent a score and then
 * putting a large green number where the score goes is worse than either
 * choice made cleanly.
 *
 * So the reliability figure is now a chip beside the names — where a caveat
 * belongs — and the headline is the six themes, which is the reading itself.
 */
import { buildChart } from '../natal/chart.js';
import { assess, PRECISIONS, VERDICT } from '../natal/uncertainty.js';
import { localToUtc } from '../natal/timeresolve.js';
import { glyphGroup } from '../natal/glyphs.js';
import * as SY from './engine.js';
import { renderBiWheel, renderAspectGrid, WHEEL_BODIES } from './biwheel.js';

const T = (window.__SY && window.__SY.T) || {};
const CFG = (window.__SY && window.__SY.cfg) || {};
/* The entry point is invoked at the BOTTOM of this file, not here.
 *
 * Two bugs lived at this spot, and the second was hidden by the first.
 *   1. The guard named `#syPanel`, copied from a sibling tool; this page has no
 *      such element, so the module loaded, imported cleanly, threw nothing and
 *      did nothing at all.
 *   2. With that fixed, calling main() here threw
 *      "Cannot access '$' before initialization": `main` is a hoisted function
 *      declaration, but the `const $` helper it uses is in the temporal dead
 *      zone until its own line runs. Hoisting the call above the consts made
 *      the module fail on its first statement.
 * Both surfaced only as "the widget never appeared". */

function esc(s) {
  return String(s === null || s === undefined ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function fill(tpl, vars) {
  return String(tpl || '').replace(/:(\w+)/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m);
}
const ae = (type, payload) => { try { if (window.__ae) window.__ae(type, payload || {}); } catch (e) {} };
const marks = {};
function funnel(step, extra) {
  if (marks[step]) return;
  marks[step] = 1;
  ae('tool_funnel', Object.assign({ step }, extra || {}));
}
const stage = (n) => { try { if (window.__stage) window.__stage(n); } catch (e) {} };
const $ = (id) => document.getElementById(id);

const people = {
  a: { lat: null, lng: null, tz: null, cityName: null },
  b: { lat: null, lng: null, tz: null, cityName: null }
};
const places = {};

function main() {
  ['a', 'b'].forEach((side) => {
    const root = $('syPlace' + side.toUpperCase());
    if (root && window.LocationInput) {
      places[side] = window.LocationInput.mount(root, {
        tool: 'synastry',
        t: (window.__SY && window.__SY.locT) || {},
        onChange: (c) => {
          people[side].lat = c.lat; people[side].lng = c.lng;
          people[side].tz = c.tz; people[side].cityName = c.cityName;
        }
      });
    }
    const unk = $('syUnknown' + side.toUpperCase());
    if (unk) unk.addEventListener('change', () => syncUnknown(side));
    syncUnknown(side);
  });

  const form = $('syForm');
  if (form) form.addEventListener('submit', (ev) => { ev.preventDefault(); run(); });

  const shared = decodeShare();
  if (shared) applyShared(shared);
  stage('form');
}

function syncUnknown(side) {
  const S = side.toUpperCase();
  const unk = $('syUnknown' + S);
  const on = unk && unk.checked;
  const time = $('syTime' + S);
  const prec = $('syPrec' + S);
  if (time) time.disabled = !!on;
  if (prec) prec.disabled = !!on;
  const note = $('syUnkNote' + S);
  if (note) note.hidden = !on;
}

/* ── input gathering ───────────────────────────────────────────────────── */
function readSide(side) {
  const S = side.toUpperCase();
  const date = ($('syDate' + S) || {}).value;
  if (!date) return { error: 'date' };
  const [y, mo, d] = date.split('-').map(Number);
  const unknown = !!($('syUnknown' + S) || {}).checked;
  const timeStr = (($('syTime' + S) || {}).value) || '12:00';
  const [h, mi] = timeStr.split(':').map(Number);
  const prec = unknown ? 'unknown' : ((($('syPrec' + S) || {}).value) || 'm5');
  const p = people[side];
  if (p.lat === null || p.lng === null) return { error: 'place' };
  return {
    y, mo, d,
    h: unknown ? 12 : h, mi: unknown ? 0 : mi,
    unknown, prec,
    lat: p.lat, lon: p.lng, tz: p.tz, cityName: p.cityName,
    name: ((($('syName' + S) || {}).value) || '').slice(0, 40)
  };
}

/**
 * The IANA zone for a birth place.
 *
 * The city picker supplies one; the manual-coordinates tab does not, and
 * passing its null straight through threw "Invalid time zone specified: null"
 * from Intl deep inside the chart builder. Falling back to UTC instead would
 * have been worse than the crash: a Moscow birth read as UTC moves every
 * placement by three hours and the tool would have said nothing.
 */
function zoneFor(side) {
  if (side.tz) return side.tz;
  try {
    if (window.__placeTime && window.__placeTime.zoneOf) {
      const z = window.__placeTime.zoneOf(side.lat, side.lon);
      if (z) return z;
    }
  } catch (e) {}
  return null;
}

async function chartFor(side) {
  if (side.error) return side;
  const tz = zoneFor(side);
  const utc = tz ? localToUtc(tz, side.y, side.mo, side.d, side.h, side.mi) : null;
  const date = (utc && utc.date) ? utc.date : new Date(Date.UTC(side.y, side.mo - 1, side.d, side.h, side.mi));
  side.resolvedTz = tz;
  // Houses need a time. With an unknown one they are not "less accurate", they
  // are undefined — so the chart is built without them and everything that
  // depends on them stays out of the comparison.
  const withHouses = !side.unknown;
  const chart = await buildChart(date, side.lat, side.lon, { withHouses });
  const minutes = PRECISIONS[side.prec] ?? PRECISIONS.m5;
  let uncertainty = null;
  try {
    uncertainty = minutes > 0 ? await assess(date, side.lat, side.lon, minutes, { withHouses }) : null;
  } catch (e) { uncertainty = null; }
  return { input: side, date, chart, uncertainty, withHouses };
}

/* ── run ───────────────────────────────────────────────────────────────── */
/* A city typed and not picked from the list is the commonest way to reach the
   button: 202 of 1980 sessions (14 days to 2026-10-16) got «Укажите место
   рождения для обоих» with the right city in the list under the field, and 44
   left. The widget now resolves the typed text the way natal-chart does;
   only a genuinely ambiguous or unknown name comes back to the person. */
let resolving = false;
async function settleTypedPlaces() {
  const out = await Promise.all(['a', 'b'].map((s) =>
    (places[s] && places[s].resolvePending) ? places[s].resolvePending() : { state: 'none' }));
  return out.find((r) => r.state === 'ambiguous' || r.state === 'unknown' || r.state === 'unavailable') || null;
}

async function run() {
  if (resolving) return;
  resolving = true;
  let stuck = null;
  try { stuck = await settleTypedPlaces(); } catch (e) { stuck = null; } finally { resolving = false; }
  if (stuck) {
    const locT = (window.__SY && window.__SY.locT) || {};
    const say = stuck.state === 'unavailable' ? (locT.city_load_error || T.err_place) : (T['err_place_' + stuck.state] || T.err_place);
    if (window.__toast) window.__toast(fill(say, { name: stuck.query }), 'error');
    return;
  }
  const a = readSide('a'), b = readSide('b');
  if (a.error || b.error) {
    const which = a.error || b.error;
    if (window.__toast) window.__toast(T['err_' + which] || T.err_form, 'error');
    return;
  }
  const btn = $('sySubmit');
  if (btn) { btn.disabled = true; btn.textContent = T.computing; }
  funnel('submit', {
    prec_a: a.prec, prec_b: b.prec,
    unknown: (a.unknown ? 1 : 0) + (b.unknown ? 1 : 0)
  });

  try {
    const [A, B] = await Promise.all([chartFor(a), chartFor(b)]);
    const points = SY.CROSS_POINTS.filter((k) =>
      (k !== 'asc' && k !== 'mc') || (A.withHouses && B.withHouses));
    const aspects = SY.annotate(
      SY.crossAspects(A.chart.points, B.chart.points, { points }),
      A.uncertainty, B.uncertainty
    );
    const th = SY.themes(aspects);
    // The full point list, not the filtered one: the headline has to count what
    // the comparison LOST, and a point dropped for lack of a birth time is
    // exactly the loss being reported.
    const dq = SY.dataQuality(aspects, {
      points: SY.CROSS_POINTS, assessA: A.uncertainty, assessB: B.uncertainty
    });
    // The overlay needs the OTHER person's houses and this person's positions,
    // so each direction carries its own assessment: a smeared planet is left
    // out rather than assigned a house it might not be in.
    const overlayAB = B.withHouses && B.chart.houses
      ? SY.houseOverlay(A.chart.points, B.chart.houses.cusps, { points, assess: A.uncertainty }) : null;
    const overlayBA = A.withHouses && A.chart.houses
      ? SY.houseOverlay(B.chart.points, A.chart.houses.cusps, { points, assess: B.uncertainty }) : null;

    render({ A, B, aspects, th, dq, overlayAB, overlayBA });
    funnel('result', {
      aspects: aspects.length, solid_share: dq.solidShare,
      usable: dq.usable ? 1 : 0, dropped: th.dropped
    });
    ae('tool_complete', { aspects: aspects.length });
    stage('result');
    location.hash = '#c=' + SY.encodeInput(
      { y: a.y, mo: a.mo, d: a.d, h: a.unknown ? null : a.h, mi: a.unknown ? null : a.mi, prec: a.prec, lat: a.lat, lon: a.lon },
      { y: b.y, mo: b.mo, d: b.d, h: b.unknown ? null : b.h, mi: b.unknown ? null : b.mi, prec: b.prec, lat: b.lat, lon: b.lon }
    );
  } catch (e) {
    if (window.__handleError) window.__handleError(e, CFG.slug || 'synastry');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = T.btn_submit; }
  }
}

/* ── render ────────────────────────────────────────────────────────────── */
function card(inner, cls) { return `<div class="sy-card${cls ? ' ' + cls : ''}">${inner}</div>`; }
function nameOf(side, fallbackKey) {
  return esc(side.input.name || T[fallbackKey]);
}

function render(r) {
  const nameA = nameOf(r.A, 'person_a'), nameB = nameOf(r.B, 'person_b');
  let html = '<div id="syReport">';
  html += headerCard(r, nameA, nameB);
  html += readingCard(r, nameA, nameB);
  html += gridCard(r, nameA, nameB);
  html += aspectsCard(r, nameA, nameB);
  if (r.overlayAB || r.overlayBA) html += overlayCard(r, nameA, nameB);
  html += methodCard(r);
  html += '</div>';
  html += card(`<div class="sy-actions">
      <button type="button" class="sy-btn" id="syPdf">${esc(T.btn_pdf)}</button>
      <button type="button" class="sy-btn ghost" id="syShare">${esc(T.btn_share)}</button>
      <button type="button" class="sy-btn ghost" id="syAgain">${esc(T.btn_again)}</button>
    </div>`);

  const out = $('syResult');
  out.innerHTML = html;
  out.hidden = false;
  out.scrollIntoView({ block: 'nearest' });

  const pdf = $('syPdf');
  if (pdf) pdf.addEventListener('click', () => {
    if (!window.__generatePDF) return;
    ae('tool_interact', { action: 'pdf' });
    window.__generatePDF({
      element: $('syReport'), filename: 'synastry.pdf', title: T.pdf_title,
      strings: { saved: T.pdf_saved, blocked: T.pdf_blocked, failed: T.pdf_failed }
    });
  });
  const sh = $('syShare');
  if (sh) sh.addEventListener('click', () => {
    const url = location.href;
    ae('tool_interact', { action: 'share' });
    if (navigator.share) { navigator.share({ title: document.title, url }).catch(() => {}); return; }
    try {
      navigator.clipboard.writeText(url).then(
        () => { if (window.__toast) window.__toast(T.link_copied, 'success'); },
        () => prompt(T.link_copied, url));
    } catch (e) { prompt(T.link_copied, url); }
  });
  const again = $('syAgain');
  if (again) again.addEventListener('click', () => {
    out.hidden = true;
    $('syForm').scrollIntoView({ block: 'nearest' });
  });
}

/**
 * Names, the reliability chip, and anything actionable that went wrong.
 *
 * The chip is deliberately small and deliberately not a lone number in white
 * space: it is a caveat about the INPUT, and a caveat rendered at headline size
 * stops being read as one. The two warnings below it stay full width because
 * they are the opposite — things the reader can fix by supplying a birth time.
 */
function headerCard(r, nameA, nameB) {
  const dq = r.dq;
  const level = dq.supportShare >= 80 ? 'good' : (dq.usable ? 'mixed' : 'poor');
  const missing = [];
  if (!r.A.withHouses) missing.push(nameA);
  if (!r.B.withHouses) missing.push(nameB);
  // A place whose zone could not be resolved was read as UTC. That can be hours
  // out, and silently is the one thing it must not be.
  const noTz = [];
  if (!r.A.input.resolvedTz) noTz.push(nameA);
  if (!r.B.input.resolvedTz) noTz.push(nameB);

  return card(
    `<div class="sy-head">
       <h2 class="sy-pair">${esc(fill(T.pair_title, { a: nameA, b: nameB }))}</h2>
       <details class="sy-chip-wrap">
         <summary class="sy-chip sy-chip-${level}">
           <span class="sy-chip-n">${dq.supportShare}%</span>
           <span class="sy-chip-l">${esc(T.quality_label)}</span>
         </summary>
         <div class="sy-chip-body">
           <p>${esc(fill(T['quality_' + level], {
              solid: dq.solid, total: dq.total, shaky: dq.shaky, unsupported: dq.unsupported
           }))}</p>
           <p class="sy-chip-note">${esc(T.quality_note)}</p>
         </div>
       </details>
     </div>
     ${missing.length ? `<p class="sy-warn">${esc(fill(T.no_houses, { who: missing.join(', ') }))}</p>` : ''}
     ${noTz.length ? `<p class="sy-warn">${esc(fill(T.no_timezone, { who: noTz.join(', ') }))}</p>` : ''}`,
    'sy-head-card');
}

/**
 * The reading itself: the wheel beside the six themes.
 *
 * Side by side rather than stacked, because they answer each other — a theme
 * that reads "a lot of it, and hard" is a cluster of red chords on the wheel,
 * and seeing the two together is the difference between a verdict and an
 * argument for one.
 */
function readingCard(r, nameA, nameB) {
  const order = ['attraction', 'warmth', 'talk', 'drive', 'duty', 'change'];
  let rows = '';
  for (const key of order) {
    const t = r.th.themes[key];
    if (!t) continue;
    if (t.ease === null) {
      rows += `<div class="sy-theme empty"><span class="sy-theme-name">${esc(T['theme_' + key])}</span>
        <span class="sy-theme-empty">${esc(T.theme_empty)}</span></div>`;
      continue;
    }
    const pct = Math.round(((t.ease + 1) / 2) * 100);
    const mood = t.ease > 0.2 ? 'easy' : (t.ease < -0.2 ? 'hard' : 'mixed');
    /* Open by default. The six sentences ARE the reading — collapsing them to
     * save height would be trading away the thing the reader came for. The
     * <details> wrapper stays so a reader who wants the six scales alone can
     * shut them, which is the compactness this needs to earn, not impose. */
    rows += `<details class="sy-theme sy-mood-${mood}" open>
      <summary>
        <span class="sy-theme-name">${esc(T['theme_' + key])}</span>
        <span class="sy-theme-act">${esc(fill(T.theme_activity, { n: t.count }))}</span>
        <span class="sy-scale"><span class="sy-scale-f" style="width:${pct}%"></span>
          <span class="sy-scale-mid" aria-hidden="true"></span></span>
      </summary>
      <p class="sy-theme-d">${esc(T['theme_' + key + '_' + mood])}</p>
      ${readingsFor(t)}
    </details>`;
  }

  let wheel = '';
  try {
    wheel = renderBiWheel(r.A.chart, r.B.chart, r.aspects, {
      size: 620, title: fill(T.wheel_title, { a: nameA, b: nameB })
    });
  } catch (e) {
    /* The wheel illustrates the reading, it is not the reading. If the geometry
     * throws — a chart with no angles because the time was unknown, a point the
     * glyph table does not carry — the themes still have to render. */
    if (window.console) console.warn('[synastry] wheel failed:', e && e.message);
  }

  return card(`<div class="sy-reading">
      ${wheel ? `<figure class="sy-wheel-wrap">${wheel}
        <figcaption class="sy-legend">
          <span class="sy-leg sy-leg-a">${esc(fill(T.wheel_inner, { who: nameA }))}</span>
          <span class="sy-leg sy-leg-b">${esc(fill(T.wheel_outer, { who: nameB }))}</span>
        </figcaption></figure>` : ''}
      <div class="sy-themes">
        <div class="sy-sec-t">${esc(T.themes_title)}</div>
        <p class="sy-sec-d">${esc(T.themes_intro)}</p>
        ${rows}
        ${r.th.dropped ? `<p class="sy-warn">${esc(fill(T.themes_dropped, { n: r.th.dropped }))}</p>` : ''}
      </div>
    </div>`);
}

/**
 * What one cross-aspect actually says, composed rather than looked up.
 *
 * Every serious competitor writes a paragraph per (point, point, aspect). With
 * fourteen directional cross-points and five aspect types that is 980 texts —
 * ten thousand strings across our ten locales, none of them checkable. A tool
 * whose whole argument is "we do not print a number we cannot derive" cannot
 * then print a thousand paragraphs it invented.
 *
 * So each point carries what it is ABOUT in a relationship and each aspect
 * carries how the two meet; the line is the two roles joined by the tone.
 * Twenty strings a locale, and it claims only what the geometry supports.
 */
/**
 * The two or three tightest aspects behind a theme, read out.
 *
 * The theme sentence says the shape ("attraction, but uneven"); these say WHAT
 * is uneven. Putting them here rather than only in the collapsed list is the
 * difference between a verdict and its evidence — and two is the limit because
 * a theme with ten aspects would otherwise become the wall the aspect list
 * already was.
 */
function readingsFor(t) {
  const top = (t.top || []).filter((a) => !a.support || a.support.supported).slice(0, 2);
  if (!top.length) return '';
  const items = top.map((a) => {
    const r = readingFor(a);
    if (!r) return '';
    /* A LABEL here, not the tone sentence: the theme's own sentence already
     * says how things go, and six themes each repeating one of five sentences
     * turns the reading into wallpaper. The full sentence is in the list. */
    const lbl = T['toneshort_' + a.aspect] || '';
    return `<li><span class="sy-why-pair">${esc(r.pair)}</span>` +
      (lbl ? `<span class="sy-why-tag sy-tag-${a.aspect}">${esc(lbl)}</span>` : '') + `</li>`;
  }).filter(Boolean).join('');
  return items ? `<ul class="sy-whys">${items}</ul>` : '';
}

function readingFor(a) {
  const ra = T['role_' + a.from], rb = T['role_' + a.to], tone = T['tone_' + a.aspect];
  if (!ra || !rb || !tone) return '';
  return { pair: fill(T.reading_pair, { ra: ra, rb: rb }), tone: tone };
}

/* Standard astrological symbols, so a reader who knows the notation needs no
 * key — and one who does not gets the whole sentence from the cell's title and
 * from the screen-reader text inside it. */
const ASPECT_SYM = {
  conjunction: '☌', opposition: '☍', trine: '△',
  square: '□', sextile: '✲'
};

function gridCard(r, nameA, nameB) {
  const has = (k) => (r.A.chart.points && r.A.chart.points[k]) || (r.B.chart.points && r.B.chart.points[k]);
  const pts = WHEEL_BODIES.filter(has);
  let grid = '';
  try {
    grid = renderAspectGrid(r.aspects, {
      rowPoints: pts, colPoints: pts,
      nameA: esc(nameA), nameB: esc(nameB),
      label: (k) => esc(T['pt_' + k] || k),
      glyph: (k) => `<svg viewBox="0 0 100 100" class="sy-g-svg" aria-hidden="true">${glyphGroup(k)}</svg>`,
      aspectName: (a) => T['asp_' + a] || a,
      symbol: ASPECT_SYM
    });
  } catch (e) {
    if (window.console) console.warn('[synastry] grid failed:', e && e.message);
    return '';
  }
  return card(`<div class="sy-sec-t">${esc(T.grid_title)}</div>
    <p class="sy-sec-d">${esc(fill(T.grid_intro, { a: nameA, b: nameB }))}</p>
    <div class="sy-grid-scroll">${grid}</div>`);
}

/**
 * The full list, collapsed.
 *
 * It used to be sixty-three open rows — the largest block on the page and the
 * least readable thing on it. It is reference material: the grid above answers
 * "is there anything between these two planets at all", and this answers
 * "exactly how tight, and does the stated birth time support it".
 */
function aspectsCard(r, nameA, nameB) {
  const shown = r.aspects.slice(0, 40);
  let rows = '';
  for (const a of shown) {
    const v = a.support ? a.support.verdict : VERDICT.STABLE;
    const badge = v === VERDICT.STABLE ? ''
      : `<span class="sy-badge sy-${v}">${esc(T['support_' + v])}</span>`;
    const r = readingFor(a);
    rows += `<li class="sy-asp${v !== VERDICT.STABLE ? ' weak' : ''}">
      <span class="sy-asp-l">${nameA} · ${esc(T['pt_' + a.from] || a.from)}</span>
      <span class="sy-asp-m">${esc(T['asp_' + a.aspect])}</span>
      <span class="sy-asp-r">${nameB} · ${esc(T['pt_' + a.to] || a.to)}</span>
      <span class="sy-asp-orb">${a.orb.toFixed(1)}°</span>${badge}
      ${r ? `<span class="sy-asp-read"><b>${esc(r.pair)}</b> ${esc(r.tone)}</span>` : ''}</li>`;
  }
  return card(`<details class="sy-fold">
    <summary><span class="sy-sec-t">${esc(fill(T.aspects_all, { n: r.aspects.length }))}</span></summary>
    <p class="sy-sec-d">${esc(fill(T.aspects_intro, { n: r.aspects.length }))}</p>
    <ol class="sy-asps">${rows}</ol></details>`);
}

function overlayCard(r, nameA, nameB) {
  const block = (ov, who, whose) => {
    if (!ov) return '';
    const items = Object.keys(ov).filter((k) => ov[k]).slice(0, 8)
      .map((k) => `<li><span>${esc(T['pt_' + k] || k)}</span><span class="sy-house">${ov[k]}</span></li>`).join('');
    return `<div class="sy-ov"><div class="sy-ov-t">${esc(fill(T.overlay_line, { who, whose }))}</div>
      <ul class="sy-ov-list">${items}</ul></div>`;
  };
  return card(`<details class="sy-fold">
    <summary><span class="sy-sec-t">${esc(T.overlay_title)}</span></summary>
    <p class="sy-sec-d">${esc(T.overlay_intro)}</p>
    ${block(r.overlayAB, nameA, nameB)}${block(r.overlayBA, nameB, nameA)}</details>`);
}

function methodCard(r) {
  return card(`<details class="sy-fold sy-method"><summary><span class="sy-sec-t">${esc(T.method_title)}</span></summary>
    <p>${esc(T.method_text)}</p>
    <p>${esc(T.method_orbs)}</p>
    <p class="sy-disclaimer">${esc(T.disclaimer)}</p></details>`);
}

function decodeShare() {
  const h = (location.hash || '').replace(/^#c=/, '');
  if (!h || h === location.hash) return null;
  return SY.decodeInput(h);
}
/* Everything above is a declaration; this is the only statement that runs on
 * load, and it runs LAST so every const it depends on is initialised. */
if (document.getElementById('syForm')) main();

function applyShared(sh) {
  const put = (side, p) => {
    const S = side.toUpperCase();
    const dd = $('syDate' + S);
    if (dd) dd.value = `${p.y}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
    const unk = $('syUnknown' + S);
    if (unk) { unk.checked = p.h === null; syncUnknown(side); }
    const tm = $('syTime' + S);
    if (tm && p.h !== null) tm.value = `${String(p.h).padStart(2, '0')}:${String(p.mi || 0).padStart(2, '0')}`;
    const pr = $('syPrec' + S);
    if (pr && p.prec && p.prec !== 'unknown') pr.value = p.prec;
    people[side].lat = p.lat; people[side].lng = p.lon;
  };
  put('a', sh.a); put('b', sh.b);
  const note = $('syShareNote');
  if (note) note.hidden = false;
  funnel('shared_open');
}
